import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { IconCheck, IconChevronDown, IconLayoutList } from "@tabler/icons-react";
import { GDL_STANDARD } from "@/lib/storage/guestDetailLayoutPref";
import { useResolvedGuestDetailLayout } from "../layout/useResolvedGuestDetailLayout";

type GuestDetailLayoutPickerProps = {
  eventId?: string;
};

/**
 * Small header dropdown to switch which guest-detail layout THIS user sees
 * ("Standard" + the event's named layouts). The pick is a per-user,
 * per-view-context preference (localStorage) — cosmetic, so every role
 * including VIEWER may use it. Renders nothing while the layout query is in
 * flight or when the event has no named layouts (nothing to switch to).
 */
export function GuestDetailLayoutPicker({ eventId }: GuestDetailLayoutPickerProps) {
  const t = useTranslations("guest-details");
  const { namedLayouts, selectedId, select, isPending } =
    useResolvedGuestDetailLayout(eventId);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click — the popup body has many nested click targets.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  if (!eventId || isPending || namedLayouts.length === 0) return null;

  const entries = [
    { id: GDL_STANDARD, name: t("layoutStandardName") },
    ...namedLayouts.map((l) => ({ id: l.id, name: l.name })),
  ];
  const selectedName =
    entries.find((e) => e.id === selectedId)?.name ?? t("layoutStandardName");

  return (
    <div ref={containerRef} className="relative" data-testid="guest-layout-picker">
      <button
        type="button"
        title={t("layoutPickerTitle")}
        onClick={() => setOpen((prev) => !prev)}
        className="flex flex-row items-center gap-1 px-1.5 py-1 rounded-lg cursor-pointer font-tertiary hover:font-primary hover:surface-tertiary transition-colors"
      >
        <IconLayoutList size={16} className="shrink-0" />
        <span className="body-4-medium max-w-32 truncate">{selectedName}</span>
        <IconChevronDown
          size={14}
          className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul className="absolute right-0 top-full mt-1 z-20 min-w-44 max-h-72 overflow-y-auto surface-primary shadow-lg rounded-lg border outline-quaternary py-1">
          {entries.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                data-testid={`guest-layout-picker-${entry.id}`}
                className="w-full flex flex-row items-center justify-between gap-2 mx-1 px-3 py-1.5 rounded-lg body-3-regular cursor-pointer hover:surface-secondary text-left"
                style={{ width: "calc(100% - 0.5rem)" }}
                onClick={() => {
                  select(entry.id);
                  setOpen(false);
                }}
              >
                <span className="truncate">{entry.name}</span>
                {entry.id === selectedId && (
                  <IconCheck size={14} className="shrink-0 font-secondary" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
